import React, { useState, useEffect, useRef } from 'react';
import { Button } from "./components/ui/button";
import KPIs_overview from './KPIs_overview';
import { SpeedInsights } from '@vercel/speed-insights/react';

(function (c, s, q, u, a, r, e) {
    c.hj = c.hj || function(){ (c.hj.q = c.hj.q || []).push(arguments) };
    c._hjSettings = { hjid: a };
    r = s.getElementsByTagName('head')[0];
    e = s.createElement('script');
    e.async = true;
    e.src = q + c._hjSettings.hjid + u;
    r.appendChild(e);
})(window, document, 'https://static.hj.contentsquare.net/c/csq-', '.js', 5349853);

{/* Progress Bar animation */}
const ProgressBar = ({ label, percentage }) => {
  const [isVisible, setIsVisible] = useState(false);
  const progressRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              if (progressRef.current) {
                progressRef.current.getBoundingClientRect();
              }
              requestAnimationFrame(() => {
                setIsVisible(true);
              });
            }, 50);
          }
        });
      },
      { threshold: 0.6 }
    );

    if (progressRef.current) {
      observer.observe(progressRef.current);
    }

    return () => {
      if (progressRef.current) {
        observer.unobserve(progressRef.current);
      }
    };
  }, []);

  return (
    <div className="mb-4" ref={progressRef}>
      <div className="flex justify-between mb-1">
        <span className="text-sm font-medium text-blue-900">{label}</span>
        <span className="text-sm font-medium text-blue-900">{percentage}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-3">
        <div
          className="bg-blue-900 h-3 rounded-full transition-all duration-1000"
          style={{ width: isVisible ? `${percentage}%` : '0%' }}
        ></div>
      </div>
    </div>
  );
};

{/* Flip card animation */}
const FlipCard = ({ frontTitle, backText }) => {
  return (
    <div className="relative w-full h-64 cursor-pointer group [perspective:1000px]">
      <div className="relative w-full h-full duration-500 transition-transform [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)]">
        {/* Front Side */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-white p-4 rounded-2xl shadow flex items-center justify-center text-center border-t-4 border-blue-900 [backface-visibility:hidden]">
          <h3 className="text-2xl font-semibold text-blue-900">{frontTitle}</h3>
        </div>
        {/* Back Side */}
        <div className="absolute inset-0 bg-blue-900 text-white p-4 rounded-2xl shadow flex flex-col justify-center items-center transition-transform [transform:rotateY(180deg)] [backface-visibility:hidden]">
          <p className="text-lg text-center">{backText}</p>
        </div>
      </div>
    </div>
  );
};

export default function PPPDeck() {
  // Load Calendly script once the component mounts
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  {/* Header */}
  return (
    <div className="flex flex-col gap-10 p-8 bg-gray-50 min-h-screen">
      <div className="text-center">
        <div className="flex justify-center">
          <img
            src="/OSL_Logo_transparent.png"
            alt="O.S.L. Equity Group Logo"
            className="rounded-2xl w-1/6 mb-6"
          />
        </div>
        <h1 className="text-4xl font-bold text-gray-900">PPP - Profitability Power Play</h1>
        <p className="mt-4 text-gray-600">Grow your company's profitability with data-driven solutions in 30 days</p>
        <p className="mt-2 text-gray-600">Join 100+ businesses already growing successfully with our proven system.</p>
        <p className="font-semibold mt-2 text-gray-600">Guaranteed Results, or get a Full Refund.</p>
      </div>
      <a
        href="https://calendly.com/d/cyqn-5t2-m3v/30-minute-discovery-call"
        target="_blank"
        rel="noopener noreferrer"
        className="-mt-5 text-center"
      >
        <Button>Book Free Strategy Call Today</Button>
      </a>

      {/* The Current Challenge */}
      <div className="shadow-xl p-6 space-y-4 bg-white rounded-2xl border-l-8 border-blue-900">
        <h2 className="text-2xl font-semibold text-blue-900">The Current Challenges</h2>
        <ul className="list-disc list-inside space-y-2 text-gray-700">
          <li>Rising operational costs impacting margins</li>
          <li>Unpredictable cash flow and financial uncertainty</li>
          <li>Underutilized staff and resources</li>
          <li>Lack of a clear strategy for scalable growth</li>
        </ul>
      </div>

      {/* Our Solution - Flip Cards on Hover */}
      <div className="shadow-xl p-6 space-y-4 bg-gradient-to-r from-gray-100 to-blue-50 rounded-2xl">
        <h2 className="text-2xl font-semibold text-blue-900">Our Solution – The PPP Playbook</h2>
        <p className="text-gray-700 text-lg">
          Our signature framework focuses on three pillars designed to transform your company's profitability, performance, and predictability.
        </p>
        <div className="flex flex-col md:flex-row justify-between gap-6 mt-6">
          <FlipCard
            frontTitle="Profitability"
            backText="Strategies include intelligent pricing optimization, proactive identification of revenue leaks, and targeted initiatives to boost cost efficiency."
          />
          <FlipCard
            frontTitle="Performance"
            backText="Elevate operational excellence by integrating strategic technologies, leveraging advanced data analytics and AI to streamline workflows, enhance insight, and drive metric-based efficiency."
          />
          <FlipCard
            frontTitle="Predictability"
            backText="Building scalable models grounded in robust forecasting and cash flow management systems, all designed to drive sustainable, long-term growth."
          />
        </div>
      </div>

      {/* Key Outcomes */}
      <div className="shadow-xl p-6 space-y-4 bg-gradient-to-r from-white to-gray-100 rounded-2xl">
        <h2 className="text-2xl font-semibold text-blue-900">Key Outcomes</h2>
        <ul className="list-disc list-inside space-y-2 text-gray-700">
          <li>Improved profitability and optimized margins</li>
          <li>Predictable cash flow and financial clarity</li>
          <li>Enhanced operational efficiency</li>
          <li>Scalability without compromising quality of service</li>
        </ul>
      </div>

      {/* Case Studies Section */}
      <section className="max-w-6xl mx-auto px-4 py-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Case Studies</h2>
        <div className="grid gap-8 md:grid-cols-2">
          {/* Case Study #1 */}
          <div className="shadow-xl p-6 space-y-4 bg-white rounded-2xl border-l-8 border-blue-900">
            <h2 className="text-2xl font-semibold text-blue-900">Interior Designer in London</h2>
            <p className="text-gray-700">
              In just <span className="font-semibold">30 days</span>, we boosted project profitability by <span className="font-semibold">23%</span>, increased upsell rates by <span className="font-semibold">18%</span>, and cut client onboarding time by <span className="font-semibold">35%</span> through strategic CRM system enhancements and an overhaul of operational workflows, driving faster processes & stronger growth.
            </p>
            <div className="mt-4">
              <ProgressBar label="Increase in Project Profitability" percentage={23} />
              <ProgressBar label="Upsell Rates Increase" percentage={18} />
              <ProgressBar label="Client Onboarding Time Reduction" percentage={35} />
            </div>
          </div>
          {/* Case Study #2 */}
          <div className="shadow-xl p-6 space-y-4 bg-white rounded-2xl border-l-8 border-blue-900">
            <h2 className="text-2xl font-semibold text-blue-900">Tophinke Elektro Kontroll AG</h2>
            <p className="text-gray-700">
              In just <span className="font-semibold">50 days</span>, we <span className="font-semibold">increased</span> net profit by <span className="font-semibold">18%</span>, reduced operational costs by <span className="font-semibold">27%</span>, and streamlined processes (through Data Analytics & AI Integration) that improved decision-making speed by <span className="font-semibold">90%</span>.
            </p>
            <div className="mt-4">
              <ProgressBar label="Net Profit Increase" percentage={18} />
              <ProgressBar label="Operational Cost Reduction" percentage={27} />
              <ProgressBar label="Decision-Making Speed Improvement" percentage={90} />
            </div>
          </div>
        </div>
      </section>
      
      {/* Calendly banner */}
      <div className="text-center">
      <h2 className="mt-3 md:mt-2 text-3xl font-bold text-gray-900">Schedule Your Call Now</h2>
      <p className="mt-1 text-gray-700">Receive your personalized Power Play Blueprint</p>
      <p className="mt-1 font-semibold text-gray-600">See Results or get your Money Back</p>
        <div
          className="mt-10 md:-mt-8 mb-10 md:-mb-2 calendly-inline-widget"
          data-url="https://calendly.com/d/cyqn-5t2-m3v/30-minute-discovery-call?hide_gdpr_banner=1"
          style={{ minWidth: "320px", height: "700px" }}
        ></div>
      </div>

      {/* Why Choose O.S.L. Equity Group */}
      <div className="shadow-xl p-6 space-y-4 bg-gradient-to-r from-gray-100 to-blue-50 rounded-2xl">
        <h2 className="text-2xl font-semibold text-blue-900">Why Businesses Choose O.S.L. Equity Group</h2>
        <ul className="list-disc list-inside space-y-2 text-gray-700">
          <li>Proven frameworks tailored for service-based businesses</li>
          <li>Data-driven strategies with measurable results</li>
          <li>Discreet, white-glove consulting approach</li>
          <li>We offer a full refund if you don’t see measurable results in 30 days. Zero risk. 100% commitment.</li>
        </ul>
      </div>

      {/* KPIs overview segment */}
      <div className="-my-3 md:-my-8">
      <KPIs_overview />
      </div>

      {/* Investment & Value */}
      <div className="shadow-xl p-6 space-y-4 bg-white rounded-2xl">
        <h2 className="text-2xl font-semibold text-blue-900">Investment & Value</h2>
        <ul className="list-disc list-inside text-gray-700">
          <li>Prioritizing and Identifying Best-Off Strategies</li>
          <li>Comprehensive Audit & Strategy Implementation</li>
          <li>Minimal work from your side, we do all the heavy lifting</li>
        </ul>
      </div>

      {/* Loyalty & Referral Incentives */}
      <div className="shadow-xl p-6 space-y-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl border-l-8 border-blue-900">
        <h2 className="text-2xl font-semibold text-blue-900">Loyalty & Referral Incentives</h2>
        <p className="text-gray-700">
          Preferred pricing for long-term partnerships and referrals. Details available upon request.
        </p>
      </div>

      {/* Meeting Button */}
      <h2 className="text-2xl font-bold text-gray-900 mt-5 text-center">Ready to get started?</h2>
      <a
        href="https://calendly.com/d/cyqn-5t2-m3v/30-minute-discovery-call"
        target="_blank"
        rel="noopener noreferrer"
        className="-mt-7 text-center"
      >
        <Button>Book Free Call Today</Button>
      </a>
      <SpeedInsights />
    </div>
  );
}
